import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Bot, History, Info, LineChart, Wand2 } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import { Prompt, useAddPrompt, useUpdatePrompt } from "@/lib/prompts-store";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const promptSchema = z.object({
  name: z.string().min(3, "Name is required"),
  content: z.string().min(10, "Prompt content is required"),
});

type PromptFormValues = z.infer<typeof promptSchema>;

interface PromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editPrompt?: Prompt;
}

const ANALYTICS_DATA = [
  { name: "v1.0", usage: 300, cost: 0.12 },
  { name: "v1.1", usage: 450, cost: 0.15 },
  { name: "v1.2", usage: 600, cost: 0.14 },
  { name: "v2.0", usage: 900, cost: 0.11 },
];

export function PromptModal({ open, onOpenChange, editPrompt }: PromptModalProps) {
  const { t } = useTranslation();
  const addPrompt = useAddPrompt();
  const updatePrompt = useUpdatePrompt();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<PromptFormValues>({
    resolver: zodResolver(promptSchema),
    defaultValues: { name: editPrompt?.name || "", content: editPrompt?.content || "" },
  });

  const handleOptimize = () => {
    const content = form.getValues("content");
    if (!content.trim()) {
      toast.info("Hãy nhập nội dung prompt trước khi tối ưu.");
      return;
    }

    form.setValue(
      "content",
      `${content.trim()}\n\nYêu cầu bổ sung:\n- Ưu tiên câu trả lời rõ ràng và có cấu trúc.\n- Nêu giới hạn khi thiếu dữ liệu.\n- Tránh suy diễn khi không có nguồn xác thực.`,
    );
    toast.success("Đã bổ sung gợi ý tối ưu cho prompt.");
  };

  const onSubmit = async (data: PromptFormValues) => {
    setIsLoading(true);
    try {
      const nextPrompt: Prompt = {
        id: editPrompt?.id || `prompt-${crypto.randomUUID()}`,
        name: data.name,
        slug: data.name.toLowerCase().replace(/\s+/g, "-"),
        content: data.content,
        model: editPrompt?.model || "GPT-4o",
        temperature: editPrompt?.temperature || 0.7,
        tags: editPrompt?.tags || ["new", "unsorted"],
        status: editPrompt?.status || "draft",
        visibility: editPrompt?.visibility || "private",
        isFavorite: editPrompt?.isFavorite || false,
        versionHistory: editPrompt
          ? [
              ...(editPrompt.versionHistory || []),
              {
                version: (editPrompt.versionHistory?.length || 0) + 1,
                content: data.content,
                timestamp: new Date().toISOString(),
              },
            ]
          : [{ version: 1, content: data.content, timestamp: new Date().toISOString() }],
        metrics: editPrompt?.metrics || { usageCount: 0, avgTokens: 0, successRate: 0 },
      };

      if (editPrompt) {
        updatePrompt(editPrompt.id, nextPrompt);
      } else {
        addPrompt(nextPrompt);
      }

      toast.success(editPrompt ? "Đã cập nhật prompt." : "Đã tạo prompt mới.");
      onOpenChange(false);
    } catch {
      toast.error("Không thể lưu prompt.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-4xl flex-col overflow-hidden border-white/5 bg-card p-0 shadow-2xl">
        <DialogHeader className="border-b border-white/5 bg-muted/30 p-6">
          <div className="flex items-center justify-between">
            <DialogTitle>{editPrompt ? `Chỉnh sửa: ${editPrompt.name}` : t("create_prompt")}</DialogTitle>
            <Button variant="outline" size="sm" className="h-8 gap-2 text-xs" type="button" onClick={handleOptimize}>
              <Wand2 className="h-3.5 w-3.5" /> {t("optimize")}
            </Button>
          </div>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <Tabs defaultValue="editor" className="flex min-h-0 flex-1 flex-col">
            <TabsList className="h-12 w-full justify-start rounded-none border-b border-white/5 bg-transparent px-6">
              <TabsTrigger value="basic">Thông tin cơ bản</TabsTrigger>
              <TabsTrigger value="editor">{t("prompt_editor")}</TabsTrigger>
              <TabsTrigger value="templates">{t("templates")}</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-6">
              <TabsContent value="basic" className="m-0 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Tên prompt</Label>
                  <Input id="name" placeholder="Ví dụ: Phản hồi hỗ trợ khách hàng lần đầu" {...form.register("name")} />
                </div>
                <p className="text-sm text-muted-foreground">
                  Đây là thông tin nhận diện chính của prompt. Metadata như tags, visibility và trạng thái đang bám theo dữ liệu hiện có trong store.
                </p>
              </TabsContent>

              <TabsContent value="editor" className="m-0 flex h-full flex-col space-y-4">
                <div className="flex flex-1 flex-col space-y-2">
                  <Label htmlFor="content">Nội dung prompt</Label>
                  <Textarea
                    id="content"
                    className="min-h-[200px] flex-1 resize-none font-mono text-xs leading-relaxed"
                    placeholder="Bạn là một trợ lý AI hữu ích. Người dùng cung cấp {{input}}, hãy phản hồi..."
                    {...form.register("content")}
                  />
                  <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Info className="h-3 w-3" /> Dùng cú pháp {"{{variable}}"} cho các đầu vào động.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="templates" className="m-0 space-y-4">
                <div className="py-12 text-center">
                  <Bot className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-4 font-semibold">Thư viện mẫu</h3>
                  <p className="text-sm text-muted-foreground">
                    Bản hiện tại chưa tách thư viện template riêng. Bạn có thể dùng prompt hiện tại làm baseline và tối ưu trực tiếp trong editor.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="analytics" className="m-0 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 font-semibold">
                      <LineChart className="h-4 w-4" /> Usage & Cost
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={ANALYTICS_DATA}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" fontSize={12} />
                          <YAxis yAxisId="left" dataKey="usage" fontSize={12} />
                          <YAxis yAxisId="right" orientation="right" dataKey="cost" fontSize={12} />
                          <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))" }} />
                          <Area yAxisId="left" type="monotone" dataKey="usage" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.22} />
                          <Area yAxisId="right" type="monotone" dataKey="cost" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.18} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 font-semibold">
                      <History className="h-4 w-4" /> {t("version_history")}
                    </h3>
                    <p className="pt-8 text-center text-sm text-muted-foreground">
                      Mỗi lần chỉnh sửa sẽ thêm một phiên bản mới vào lịch sử prompt trong store cục bộ để bạn dễ so sánh và phục hồi.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="mt-auto border-t border-white/5 bg-muted/30 p-6">
            <Button variant="ghost" onClick={() => onOpenChange(false)} type="button">
              {t("cancel")}
            </Button>
            <Button type="submit" className="min-w-[120px] bg-primary text-primary-foreground hover:bg-primary/90" disabled={isLoading}>
              {isLoading ? "Đang lưu..." : "Lưu prompt"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
